/**
 *
 * Visuals
 *
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Text, EmptyState } from 'components/common';
import Placeholder from 'assets/images/visuals-placeholder.png';
import { SkeletonLoader } from 'components';
import { useQuery } from 'react-query';
import { fetchVisuals, fetchVisualization } from './api';
import styles from './styles.module.css';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
// eslint-disable-next-line sonarjs/cognitive-complexity
export const Visuals = () => {
  const [vizList, setVizList] = useState<null | any[]>(null);
  const history = useHistory();
  const isVisualsListPage = useMemo(
    () => history.location.pathname.split('/').length === 2,
    [history.location.pathname],
  );

  const { data: visualsList, isLoading: isFetchingVisuals } = useQuery(
    'visualsList',
    fetchVisuals,
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: isVisualsListPage,
    },
  );

  /**
   *  Fill graph as per the filter applied via API call and prep the cache.
   *  */
  const { data: singleVisual } = useQuery(
    ['singleVisual', history.location.pathname],
    () => fetchVisualization(history.location.pathname),
    {
      staleTime: Infinity,
      cacheTime: 0,
      enabled: !isVisualsListPage,
    },
  );

  const moveToVisuals = (visual: any) => {
    history.push({
      pathname: `/${history.location.pathname.split('/')[1]}/${visual.id}`,
    });
  };

  useEffect(() => {
    if (Array.isArray(visualsList)) {
      const list = visualsList.map((visual) => {
        let imgLink;
        if (visual.thumbnail) imgLink = visual.thumbnail;
        else imgLink = Placeholder;
        let padding;
        if (!visual.thumbnail) padding = '25px 0px';
        else padding = '1px';

        return (
          <div
            key={visual.id}
            className={styles.visualCard}
            onClick={() => moveToVisuals(visual)}
          >
            <Text text={visual.name} type="subtitleLarge" />
            <div
              className={styles.thumbnail}
              style={{
                backgroundImage: `url(${imgLink})`,
                padding,
              }}
            />
          </div>
        );
      });
      setVizList(list);
    } else {
      setVizList([]);
    }
  }, [visualsList]);

  /**
   * Validate, URL
   * @param url : URL string
   * @returns : Boolean, whether URL is valid or not
   */
  const validateURL = (url: string) => {
    const URLRegex = /^(https:|http:)/;
    return URLRegex.test(url);
  };

  /**
   * Renders a particular Visualization.
   * @returns {JSX}
   */
  const showSelectedViz = () => {
    if (singleVisual && singleVisual.url && validateURL(singleVisual.url)) {
      return (
        <iframe
          key={`${singleVisual.name}`}
          id={`${singleVisual.id}`}
          title={`${singleVisual.name}`}
          name={`${singleVisual.name}}`}
          frameBorder="0"
          src={`${singleVisual.url}?:showVizHome=no&:embed=true&:display_count=yes&:toolbar=no&:origin=viz_share_link`}
          style={{
            width: '100%',
            height: 'calc(100vh - 50px)',
          }}
        />
      );
    }
    return <div />;
  };

  /**
   * List of all visuals present.
   * @returns {JSX}
   */
  const showVizList = () => {
    if (isFetchingVisuals || vizList === null) {
      // adding dummy data for skeleton loaders
      return (
        <div className={styles.skeletonLoader}>
          {[...Array(3).keys()].map((itm: number) => (
            <div
              key={`${itm}-${new Date().getTime()}`}
              className={styles.visualCard}
            >
              <SkeletonLoader type="line" width={60} height={15} />
              <SkeletonLoader type="line" width={280} height={140} />
            </div>
          ))}
        </div>
      );
    }
    if (vizList !== null && vizList.length === 0) {
      return (
        <div className={styles.container}>
          <EmptyState
            message={`${
              history.location.pathname.includes('agent-visualizations')
                ? 'Agent Productivity related v'
                : 'V'
            }isualizations show up here`}
          />
        </div>
      );
    }
    if (vizList !== null && !isFetchingVisuals && vizList.length > 0) {
      return <div className={styles.listContainer}>{vizList}</div>;
    }
    return <div />;
  };

  return !isVisualsListPage ? (
    <div className={styles.container}>{showSelectedViz()}</div>
  ) : (
    showVizList()
  );
};

export default Visuals;
